/**
 * An in-app numeric pad for LENGTH fields (owner, 2026-08-04).
 *
 * THE PROBLEM
 * Tapping a length field on a tablet summoned the full QWERTY keyboard, which
 * covered the side panel and made entry and navigation awkward. The obvious fix
 * — `inputmode="numeric"` — is wrong here: `parseLength` accepts `120 1/2`,
 * `94 3/16`, `8'6"` and `8-6`, and a numeric keypad has no `/`, no space and no
 * quote marks. Designers work from tape measures, so fractions are a
 * requirement, not a nicety (owner). Hence our own pad, with the keys this app
 * actually needs.
 *
 * WHY IT DISPATCHES `change`
 * Fifteen length fields already commit on `change` (and some on Enter). The pad
 * writes the value and fires a real `change` event, so every one of those
 * handlers keeps working untouched — no per-field rewiring, and no second
 * commit path to drift out of step with the first. Its Done key is therefore
 * also an explicit commit, which is what fixes the crown field that could never
 * be changed.
 *
 * TOUCH ONLY
 * On a fine pointer (a mouse) the pad stays out of the way entirely and the
 * physical keyboard behaves exactly as before. Suppressing a keyboard nobody
 * summoned would be a regression.
 */

/** Fractions a tape measure actually shows, in tap order. */
const FRACTIONS = [
  '1/16', '1/8', '3/16', '1/4', '5/16',
  '3/8', '7/16', '1/2', '9/16', '5/8',
  '11/16', '3/4', '13/16', '7/8', '15/16',
];

/**
 * Appends a fraction to a typed length, inserting the space that separates a
 * whole number from its fraction — `120` + `1/2` → `120 1/2`, which is what
 * `parseLength` expects. Without the space it would read as `1201/2`.
 *
 * Exported for its own tests: the space rule is the one piece of real logic
 * here, and it is easy to get wrong at the boundaries (empty field, a value
 * already ending in a space, a feet mark).
 *
 * @param {string} value current field text
 * @param {string} fraction e.g. "1/2"
 * @returns {string}
 */
export function appendFraction(value, fraction) {
  const text = String(value ?? '');
  if (!text.trim()) return fraction;
  // Already ends in whitespace: the separator is there, do not double it.
  if (/\s$/.test(text)) return `${text}${fraction}`;
  // A digit or a foot/inch mark before the fraction needs the separating space.
  if (/[\d'"]$/.test(text)) return `${text} ${fraction}`;
  return `${text}${fraction}`;
}

/** Appends a math operator with the spaces that preserve fraction/shorthand parsing. */
export function appendLengthOperator(value, operator) {
  const text = String(value ?? '').trimEnd();
  const symbol = ({ '+': '+', '-': '-', '×': '*', '*': '*', '÷': '/', '/': '/' })[operator];
  if (!text || !symbol) return text;
  return `${text} ${symbol} `;
}

/**
 * How far in from the viewport's right edge the pad should sit, in px.
 *
 * The pad was pinned bottom-right "so it clears the drawing", but every length
 * field lives in the right-hand side panel — so on a tablet the pad covered
 * the panel it was serving and entry meant typing blind (owner, 2026-08-09).
 * The owner's call: park it DIRECTLY LEFT OF THE SIDE PANEL — "it goes away
 * when not active so not really an issue if it covers a portion of the
 * drawing". One predictable spot beats cleverness: the pad is always in the
 * same place, immediately beside the field being typed into.
 *
 * Falls back to the plain right dock when there is no panel to sit beside —
 * a hidden panel, or the narrow stacked layout where the panel is BELOW the
 * drawing and nothing horizontal is in the way.
 *
 * Exported for its own tests — this is the one piece of real logic here.
 *
 * @param {{left:number, width:number}|null} panelBox the side panel's rect
 * @param {number} viewportWidth
 * @param {number} gap breathing room between pad and panel, px
 * @returns {number} px offset for the pad's `right`
 */
export function padRightOffsetPx(panelBox, viewportWidth, gap = 12) {
  const width = viewportWidth || 0;
  const left = panelBox?.left;
  // No panel, a zero-width (hidden) panel, or a panel that is not to the side
  // (stacked layout puts it below, so its left edge is at the viewport edge).
  if (!panelBox || !(panelBox.width > 0) || !Number.isFinite(left) || left <= 0) return gap;
  return Math.max(gap, width - left + gap);
}

/**
 * Installs the pad. Length inputs opt in with `data-pad="length"`, and the
 * listener is DELEGATED from the document — the side panel rebuilds its inputs
 * constantly, so per-field wiring would need re-applying on every render and
 * would eventually miss one.
 *
 * @param {Document} root
 * @param {{shouldOpen?:(input:HTMLInputElement)=>boolean}} [options]
 * @returns {{ isActive: () => boolean }}
 */
export function mountNumericPad(root, { shouldOpen = () => true } = {}) {
  // A mouse user gets nothing: no suppressed keyboard, no pad, no change.
  const coarse = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;
  if (!coarse) return { isActive: () => false };

  let target = null;   // the input being edited
  let wrote = false;   // the pad has changed `target`'s text since it opened
  let openedWith = ''; // that text before it did, so Escape can put it back
  const pad = root.createElement('div');
  pad.className = 'numpad';
  pad.hidden = true;
  pad.setAttribute('role', 'group');
  pad.setAttribute('aria-label', 'Number pad');

  /** Writes at the caret when there is one, else appends. */
  const insert = (text) => {
    if (!target) return;
    const start = target.selectionStart;
    const end = target.selectionEnd;
    if (typeof start === 'number' && typeof end === 'number') {
      target.setRangeText(text, start, end, 'end');
    } else {
      target.value += text;
    }
    // `input` keeps any live validation in step; `change` is saved for commit,
    // so a half-typed value never reaches the model.
    wrote = true;
    target.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const backspace = () => {
    if (!target) return;
    const start = target.selectionStart;
    const end = target.selectionEnd;
    if (typeof start === 'number' && typeof end === 'number' && start !== end) {
      target.setRangeText('', start, end, 'end');
    } else if (typeof start === 'number' && start > 0) {
      target.setRangeText('', start - 1, start, 'end');
    } else {
      target.value = target.value.slice(0, -1);
    }
    wrote = true;
    target.dispatchEvent(new Event('input', { bubbles: true }));
  };

  /**
   * Lets go of the current field, handing what was typed to that field's OWN
   * change handler on the way out.
   *
   * THIS IS THE WHOLE FIX for the owner's 2026-08-30 report — "not rendering
   * correctly, stuck on old stale info" on drop-down attic stairs. The stairs
   * were incidental. The pad writes through `setRangeText` / `value +=`, which
   * is a PROGRAMMATIC edit: the browser does not count it as user input, so it
   * raises no `change` of its own when focus leaves. Every length field in this
   * app commits on `change`. So every dismissal that was not the Done key left
   * the figure sitting visibly in the box while the model, the drawing and the
   * saved document all kept the previous one — and a designer filling a card
   * field by field, as the owner was, committed nothing but their last tap.
   *
   * Which is why release() is called from all three ways out rather than fixed
   * at one of them: hide(), the Done key, and show() when the pad is handed
   * from one field to the next.
   *
   * @param {'auto'|'always'|'never'} mode
   *   `auto` commits only if the pad actually wrote something — so opening the
   *   pad and thinking better of it never stamps the document as edited.
   *   `always` is the Done key, which stays an explicit commit.
   *   `never` is Escape: it puts back the text the field had when the pad
   *   opened, because a field left showing a figure the designer abandoned is
   *   the same panel-disagrees-with-drawing problem by another route.
   * @returns {HTMLInputElement|null} the field just released
   */
  const release = (mode = 'auto') => {
    const input = target;
    const dirty = wrote;
    const before = openedWith;
    // Cleared BEFORE dispatching: a change handler routinely rebuilds the panel
    // the field lives in, and anything it triggers must not find the pad still
    // holding a node that is on its way out of the DOM.
    target = null;
    wrote = false;
    openedWith = '';
    if (!input) return null;
    if (mode === 'never') {
      if (dirty) {
        input.value = before;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      return input;
    }
    if (mode === 'always' || dirty) input.dispatchEvent(new Event('change', { bubbles: true }));
    return input;
  };

  const commit = () => {
    if (!target) return;
    const input = target;
    close();
    release('always');
    input.blur();
  };

  const key = (label, onTap, cls = '') => {
    const b = root.createElement('button');
    b.type = 'button';
    b.className = `numpad__key ${cls}`.trim();
    b.textContent = label;
    b.setAttribute('aria-label', label);
    // pointerdown + preventDefault so the field never loses focus (and the
    // caret never moves) when a key is pressed.
    b.addEventListener('pointerdown', (evt) => { evt.preventDefault(); onTap(); });
    return b;
  };

  const digits = root.createElement('div');
  digits.className = 'numpad__digits';
  for (const d of ['7', '8', '9', '4', '5', '6', '1', '2', '3', '0']) {
    digits.append(key(d, () => insert(d)));
  }
  digits.append(key('.', () => insert('.')));
  digits.append(key('space', () => insert(' '), 'numpad__key--wide'));

  const marks = root.createElement('div');
  marks.className = 'numpad__marks';
  marks.append(
    key('/', () => insert('/')),
    key('′', () => insert("'")), // feet
    key('″', () => insert('"')), // inches
    key('-', () => insert('-')),
  );

  const operators = root.createElement('div');
  operators.className = 'numpad__operators';
  for (const symbol of ['+', '−', '×', '÷']) {
    operators.append(key(symbol, () => {
      if (!target) return;
      const normalized = symbol === '−' ? '-' : symbol;
      target.value = appendLengthOperator(target.value, normalized);
      target.dispatchEvent(new Event('input', { bubbles: true }));
    }, 'numpad__key--operator'));
  }

  const fracs = root.createElement('div');
  fracs.className = 'numpad__fractions';
  for (const f of FRACTIONS) {
    fracs.append(key(f, () => {
      if (!target) return;
      target.value = appendFraction(target.value, f);
      target.dispatchEvent(new Event('input', { bubbles: true }));
    }, 'numpad__key--frac'));
  }

  const actions = root.createElement('div');
  actions.className = 'numpad__actions';
  actions.append(
    key('⌫', backspace, 'numpad__key--util'),
    key('Clear', () => {
      if (!target) return;
      target.value = '';
      target.dispatchEvent(new Event('input', { bubbles: true }));
    }, 'numpad__key--util'),
    key('Done', commit, 'numpad__key--done'),
  );

  const title = root.createElement('p');
  title.className = 'numpad__title';

  pad.append(title, fracs, digits, marks, operators, actions);
  root.body.append(pad);

  /** Park the pad immediately left of the side panel (see padRightOffsetPx). */
  function dockBesidePanel() {
    const panel = root.querySelector('.table-panel');
    const box = panel && panel.offsetParent !== null ? panel.getBoundingClientRect() : null;
    pad.style.right = `${padRightOffsetPx(box, root.defaultView?.innerWidth ?? 0)}px`;
  }

  function show(input) {
    // Hand-off between two length fields: the one being LEFT commits first.
    // Without this, tapping straight from field to field down a card — the
    // ordinary way to fill one in — dropped every figure but the last.
    if (target && target !== input) release();
    // …and that commit may have rebuilt the panel, taking the very node the
    // fingertip landed on with it. Arming a detached input would leave the pad
    // open over a field nobody can see, typing into nothing.
    if (input.isConnected === false) { close(); return; }
    target = input;
    wrote = false;
    openedWith = input.value;
    title.textContent = input.getAttribute('aria-label') || input.placeholder || 'Length';
    pad.hidden = false;
    dockBesidePanel();
    // Keep the field itself visible: the pad is docked, so a field low in the
    // panel would otherwise sit behind it — the exact complaint about the OS
    // keyboard.
    requestAnimationFrame(() => input.scrollIntoView({ block: 'center', behavior: 'smooth' }));
  }

  /** Puts the pad away. Presentation only — it lets go of nothing. */
  function close() {
    pad.hidden = true;
  }

  /** Puts the pad away AND releases the field, per `mode` (see release()). */
  function hide(mode = 'auto') {
    release(mode);
    close();
  }

  root.addEventListener('focusin', (evt) => {
    const input = evt.target?.closest?.('[data-pad="length"]');
    if (input && shouldOpen(input)) show(input);
    else if (!pad.contains(evt.target)) hide();
  });

  // A tap anywhere that is neither the pad nor a pad-driven field closes it.
  root.addEventListener('pointerdown', (evt) => {
    if (pad.hidden) return;
    if (pad.contains(evt.target)) return;
    if (evt.target?.closest?.('[data-pad="length"]')) return;
    hide();
  }, true);

  root.addEventListener('keydown', (evt) => {
    if (pad.hidden) return;
    if (evt.key === 'Escape') hide('never'); // cancel: put the figure back
    if (evt.key === 'Enter') commit();
  });

  return { isActive: () => !pad.hidden };
}
